import { type Component, Show } from 'solid-js'
import { z } from 'zod'
import Button from '~/components/Button'
import Form from '~/components/Form'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Textarea from '~/components/Textarea'
import { useToast } from '~/components/useToast'
import { useForm } from '~/hooks/useForm'
import { catalogService } from '~/services/catalog.service'
import type { QuoteRequestModalPropsType } from '~/types/QuoteRequestModal'
import { resolveError } from '~/utils/error'

const schema = z
  .object({
    name: z.string().trim().min(2, 'Dinos cómo te llamas'),
    email: z.string().trim().email('Revisa el correo').or(z.literal('')),
    phone: z.string().trim(),
    message: z.string().trim()
  })
  // Con uno de los dos basta, pero sin ninguno no hay forma de responder.
  .refine(v => v.email !== '' || v.phone !== '', {
    message: 'Déjanos un correo o un teléfono para responderte',
    path: ['email']
  })

/**
 * Solicitud de cotización desde la ficha pública. No cobra ni reserva nada: crea
 * la cotización en borrador para que la empresa la revise, y al visitante lo
 * deja registrado como cliente.
 */
const QuoteRequestModal: Component<QuoteRequestModalPropsType> = props => {
  const toast = useToast()

  const form = useForm({
    schema,
    initialValues: () => ({ name: '', email: '', phone: '', message: '' }),
    onSubmit: async values => {
      const res = await catalogService.requestQuote(props.empresa, props.productKey, {
        name: values.name,
        email: values.email || null,
        phone: values.phone || null,
        quantity: props.quantity,
        variantId: props.variantId ?? null,
        message: values.message || null
      })

      toast.addToast({ title: res.message ?? 'Solicitud enviada', type: 'success' })
      props.onSent?.(res.data?.number)
      props.onClose()
    },
    onError: err => toast.addToast({ title: resolveError(err), type: 'danger' })
  })

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="sm" closable>
      <Modal.Header>
        <div>
          <h2 class="text-lg font-bold text-foreground">Solicitar cotización</h2>
          <p class="text-sm text-foreground-subtle">{props.productName}</p>
        </div>
      </Modal.Header>

      <Modal.Body>
        <Form id="quote-request-form" controller={form}>
          <div class="space-y-4">
            <Input name="name" label="Nombre" placeholder="Tu nombre" />
            <Input name="email" label="Correo" type="email" placeholder="tucorreo@ejemplo.com" />
            <Input name="phone" label="Teléfono / WhatsApp" placeholder="300 000 0000" />
            <Textarea name="message" label="¿Algo que debamos saber?" rows={3} placeholder="Opcional" />

            <Show when={props.variantLabel}>
              <p class="text-xs text-foreground-subtle">
                Opción seleccionada: <span class="text-foreground font-medium">{props.variantLabel}</span>
              </p>
            </Show>
            <Show when={props.quantity && props.quantity > 1}>
              <p class="text-xs text-foreground-subtle">Cantidad: {props.quantity}</p>
            </Show>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div class="flex justify-end gap-2">
          <Button variant="text" color="default" onClick={props.onClose}>
            Cancelar
          </Button>
          {/* El form vive fuera del footer: sin `form` el botón no lo enviaría. */}
          <Button color="primary" type="submit" form="quote-request-form" loading={form.submitting()}>
            Enviar solicitud
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default QuoteRequestModal
